import { get } from 'lodash';
import { useCallback } from 'react';
import {
    type Control,
    Controller,
    type FieldError,
    type FieldErrors,
    type FieldValues,
    type Path,
    type UseFormRegister,
} from 'react-hook-form';
import type {
    NativeSyntheticEvent,
    TextInputFocusEventData,
} from 'react-native';
import type { GetProps, StackProps } from 'tamagui';
import { YStack } from 'tamagui';

import type { Input, InputProps } from '../inputs/Input';
import { FootnoteRegular, LabelRegular } from '../text/Typography';
import {
    ControlledInputWrapper,
    ERROR_HINT_TEXT_COLOR,
    ERROR_TEXT_COLOR,
    HINT_TEXT_COLOR,
    LABEL_TEXT_COLOR,
} from './ControlledInput';

export interface FormatterResult {
    displayValue: string;
    storedValue: string;
}

export type FormatterFunction = (
    input: string,
    previousValue?: string,
) => FormatterResult;

interface ControlledInputFieldProps {
    label?: string;
    hint?: string;
    error?: { message?: string };
    containerStyle?: StackProps;
    children: React.ReactNode;
}

const ControlledInputField = ({
    label,
    hint,
    error,
    containerStyle,
    children,
}: ControlledInputFieldProps) => {
    const isError = error != null;

    return (
        <YStack gap={8} {...containerStyle}>
            {label != null && (
                <LabelRegular
                    color={isError ? ERROR_TEXT_COLOR : LABEL_TEXT_COLOR}
                >
                    {label}
                </LabelRegular>
            )}
            {children}
            {hint != null && (
                <LabelRegular
                    color={isError ? ERROR_HINT_TEXT_COLOR : HINT_TEXT_COLOR}
                >
                    {hint}
                </LabelRegular>
            )}
            {isError && (
                <FootnoteRegular color={ERROR_TEXT_COLOR}>
                    {error?.message}
                </FootnoteRegular>
            )}
        </YStack>
    );
};

interface ControlledFormattedInputProps<
    TFieldValues extends FieldValues,
    TContext,
> extends Omit<GetProps<typeof Input>, 'formatValueOnChange'> {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
    formatter: FormatterFunction;
    InputComponent: React.ComponentType<InputProps>;
    inputComponentProps?: Record<string, unknown>;
    label?: string;
    hint?: string;
    containerStyle?: StackProps;
    rightSideContent?: React.ReactNode;
}

export const ControlledFormattedInput = <
    TFieldValues extends FieldValues,
    TContext,
>({
    name,
    control,
    register,
    label,
    hint,
    errors,
    formatter,
    InputComponent,
    inputComponentProps = {},
    containerStyle,
    rightSideContent,
    onFocus,
    ...props
}: ControlledFormattedInputProps<TFieldValues, TContext>) => {
    const error = get(errors, name) as FieldError | undefined;
    const isError = error != null;

    const handleFocus = useCallback(
        (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
            e.preventDefault();
            onFocus?.(e);
        },
        [onFocus],
    );

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                ...register,
            }}
            render={({ field: { onChange, ref, onBlur, value } }) => {
                const getDisplayValue = (val: string): string => {
                    // Call formatter even for empty values to allow formatters to handle empty state
                    const result = formatter(val || '', value);
                    return result.displayValue;
                };

                return (
                    <ControlledInputField
                        label={label}
                        hint={hint}
                        error={
                            error != null
                                ? {
                                      message: (error as { message?: string })
                                          .message,
                                  }
                                : undefined
                        }
                        containerStyle={containerStyle}
                    >
                        <ControlledInputWrapper
                            rightSideContent={rightSideContent}
                        >
                            <InputComponent
                                ref={ref}
                                onBlur={onBlur}
                                onFocus={handleFocus}
                                invalid={isError}
                                value={getDisplayValue(value)}
                                onChangeText={(text: string) => {
                                    const result = formatter(text, value);
                                    onChange(result.storedValue);
                                }}
                                {...inputComponentProps}
                                {...props}
                            />
                        </ControlledInputWrapper>
                    </ControlledInputField>
                );
            }}
        />
    );
};
